/**
 * "Leroy is reading your skin" — the interstitial shown while the AI job runs.
 *
 * The status text cycles for reassurance, but the progress bar is driven by the
 * REAL job status coming back from pollAIStatus, not by a timer — so it cannot
 * sit at 100% while the job is still queued, or finish before the result exists.
 */
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';

const LIME = '#BDD630';
const LIME_LIGHT = '#c5e47a';

const STATUSES = [
  'Mapping surface texture…',
  'Assessing hydration and barrier…',
  'Detecting tone and pigmentation…',
  'Weighing your concerns and triggers…',
  'Building your regimen…',
];

/** Real job phases → how far along we honestly are. */
const PROGRESS_BY_STATUS: Record<string, number> = {
  queued: 12,
  pending: 12,
  processing: 55,
  analyzing: 70,
  completed: 100,
};

export interface ScanningStageProps {
  /** Live job status from pollAIStatus. */
  jobStatus?: string | null;
  /** Photo to scan over, if the user supplied one. */
  photoUrl?: string | null;
  error?: string | null;
  onRetry?: () => void;
}

export function ScanningStage({ jobStatus, photoUrl, error, onRetry }: ScanningStageProps) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (error) return;
    const t = setInterval(() => setIdx(i => (i + 1) % STATUSES.length), 2600);
    return () => clearInterval(t);
  }, [error]);

  const progress = PROGRESS_BY_STATUS[String(jobStatus ?? 'queued').toLowerCase()] ?? 12;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #1a3d2a 0%, #12220c 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '44px 28px',
      }}
    >
      <style>{`
        @keyframes marple-scanline {
          0%   { top: 0%;   opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes marple-pin {
          0%, 100% { transform: scale(1);   opacity: .55; }
          50%      { transform: scale(1.5); opacity: 1; }
        }
      `}</style>

      <div style={{ textAlign: 'center', maxWidth: 460, width: '100%' }}>
        {/* Scan surface */}
        <div
          style={{
            position: 'relative',
            width: 240,
            aspectRatio: '3/4',
            margin: '0 auto 28px',
            borderRadius: 16,
            overflow: 'hidden',
            border: '2px solid rgba(189,214,48,.35)',
            background: photoUrl ? undefined : 'rgba(255,255,255,.05)',
          }}
        >
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: 'rgba(255,255,255,.35)', fontSize: 13 }}>
              Analysing your responses
            </div>
          )}

          {!error && (
            <>
              {/* Scan line */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, transparent, ${LIME}, transparent)`,
                  boxShadow: `0 0 14px ${LIME}`,
                  animation: 'marple-scanline 2.6s cubic-bezier(.4,0,.2,1) infinite',
                }}
              />
              {/* Concern pins */}
              {[
                { top: '28%', left: '32%', d: '0s' },
                { top: '44%', left: '66%', d: '.5s' },
                { top: '62%', left: '40%', d: '1s' },
                { top: '34%', left: '54%', d: '1.5s' },
              ].map(p => (
                <span
                  key={`${p.top}-${p.left}`}
                  style={{
                    position: 'absolute',
                    top: p.top,
                    left: p.left,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    border: `2px solid ${LIME}`,
                    animation: `marple-pin 2s ease-in-out ${p.d} infinite`,
                  }}
                />
              ))}
            </>
          )}
        </div>

        {error ? (
          <>
            <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#E07A5F' }} />
            <h2 className="font-serif normal-case" style={{ fontSize: 26, color: '#fff', margin: '0 0 10px' }}>
              Leroy couldn't finish your reading
            </h2>
            <p style={{ color: 'rgba(255,255,255,.7)', fontSize: 15, margin: '0 0 22px' }}>{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-7 py-3 rounded-full font-semibold transition-transform hover:scale-105"
                style={{ background: LIME, color: '#1A2E12' }}
              >
                Try again
              </button>
            )}
          </>
        ) : (
          <>
            <h2 className="font-serif normal-case" style={{ fontSize: 28, color: '#fff', margin: '0 0 10px' }}>
              Leroy is reading your skin
            </h2>
            <p style={{ color: LIME_LIGHT, fontSize: 15, minHeight: 22, transition: 'opacity .3s' }}>
              {STATUSES[idx]}
            </p>
            <div style={{ width: 220, margin: '18px auto 0' }}>
              <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,.1)', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: LIME,
                    borderRadius: 999,
                    transition: 'width 1.1s cubic-bezier(.4,0,.2,1)',
                  }}
                />
              </div>
            </div>
            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 12, marginTop: 14 }}>
              This usually takes under a minute. You can leave this page — your reading is saved to your profile.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ScanningStage;
